import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { List, X, Translate } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else if (el) el.scrollIntoView({ behavior: "smooth" });
};

const LangToggle = ({ lang, toggle, testid, className }) => (
  <button
    data-testid={testid}
    onClick={toggle}
    className={`flex items-center gap-1.5 rounded-full glass border border-white/60 px-3 py-2 text-xs font-bold text-foreground/70 hover:text-primary transition-colors ${className || ""}`}
  >
    <Translate weight="bold" size={16} />
    {lang === "es" ? "EN" : "ES"}
  </button>
);

export default function Header() {
  const navigate = useNavigate();
  const { lang, toggle, t } = useLang();
  const [open, setOpen] = useState(false);

  const navLinks = [
    { label: t("nav.como"), id: "como-funciona" },
    { label: t("nav.ejemplos"), id: "ejemplos" },
    { label: t("nav.paquetes"), id: "paquetes" },
    { label: t("nav.crear"), id: "crear" },
    { label: t("nav.faq"), id: "faq" },
  ];

  const handleScroll = (id) => {
    setOpen(false);
    setTimeout(() => scrollTo(id), 50);
  };

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 120, damping: 18 }}
      className="fixed top-0 left-0 right-0 z-[80] glass border-b border-white/50"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-2"
        >
          <img src="/logo.png" alt="Regala Una Melodía" className="h-11 w-auto" />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm font-bold text-foreground/70 hover:text-primary transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-seguimiento"
            onClick={() => navigate("/seguimiento")}
            className="hidden sm:block text-sm font-bold text-foreground/70 hover:text-primary transition-colors"
          >
            {t("nav.seguimiento")}
          </button>
          <button
            data-testid="nav-review"
            onClick={() => navigate("/review")}
            className="hidden sm:block text-sm font-bold text-foreground/70 hover:text-primary transition-colors"
          >
            {t("nav.review")}
          </button>
          <LangToggle lang={lang} toggle={toggle} testid="lang-toggle" className="hidden sm:flex" />
          <button
            data-testid="nav-admin"
            onClick={() => navigate("/admin/login")}
            className="hidden sm:block text-xs font-semibold text-foreground/40 hover:text-foreground/70"
          >
            {t("nav.admin")}
          </button>
          <motion.button
            data-testid="header-cta"
            whileHover={{ scale: 1.05, rotate: -1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollTo("crear")}
            className="hidden sm:block rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-soft"
          >
            {t("nav.cta")}
          </motion.button>

          {/* Móvil: toggle idioma + hamburguesa */}
          <LangToggle lang={lang} toggle={toggle} testid="lang-toggle-mobile" className="sm:hidden" />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            className="md:hidden flex items-center justify-center w-11 h-11 rounded-full bg-primary text-primary-foreground shadow-soft"
          >
            {open ? <X weight="bold" size={22} /> : <List weight="bold" size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 26 }}
            className="md:hidden overflow-hidden glass border-t border-white/50"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {navLinks.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => handleScroll(l.id)}
                  className="text-left rounded-2xl px-4 py-3 text-base font-bold text-foreground/80 hover:bg-primary/10 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-seguimiento"
                onClick={() => go("/seguimiento")}
                className="text-left rounded-2xl px-4 py-3 text-base font-bold text-foreground/80 hover:bg-primary/10 transition-colors"
              >
                {t("nav.seguimiento")}
              </button>
              <button
                data-testid="mobile-nav-review"
                onClick={() => go("/review")}
                className="text-left rounded-2xl px-4 py-3 text-base font-bold text-foreground/80 hover:bg-primary/10 transition-colors"
              >
                {t("nav.review")}
              </button>
              <button
                data-testid="mobile-nav-admin"
                onClick={() => go("/admin/login")}
                className="text-left rounded-2xl px-4 py-3 text-sm font-semibold text-foreground/40 hover:bg-primary/10 transition-colors"
              >
                {t("nav.admin")}
              </button>
              <button
                data-testid="mobile-cta"
                onClick={() => handleScroll("crear")}
                className="mt-2 rounded-full bg-primary px-5 py-3.5 text-base font-bold text-primary-foreground shadow-soft"
              >
                {t("nav.ctaEmoji")}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
